import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";

const PROFILE_IMAGE = "https://storage.readdy-site.link/project_files/970e7742-95b9-4b95-9fff-25606ee6395a/8d30e508-e1d7-40d7-bdf7-63162cd50953_S__116810082.jpg?v=dc59a25b2a68b1c48a2c584183911f38";

const guests = [
  "Peter Van Hoesen",
  "Patrick Russell",
  "Eric Cloutier",
  "Sunju Hargun",
  "Priori",
  "Innerworld",
  "Sindh",
  "Shoal",
];

const tours = [
  { year: "2025", detail: "India Tour — GOA & Bangalore" },
  { year: "2026", detail: "Korea Tour (Upcoming)" },
];

const IRON_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Iron",
  jobTitle: "DJ",
  description:
    "東京拠点のテクノDJ。陰陽共鳴の摂理をベースにTrance Tripを追求。SAMSARAのレジデントとして活動し、インドツアーを完遂。2026年韓国ツアーも控える。",
  homeLocation: {
    "@type": "Place",
    name: "Tokyo, Japan",
    geo: { "@type": "GeoCoordinates", latitude: 35.6762, longitude: 139.6503 },
  },
  url: `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/artist/iron`,
};

export default function IronPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  /* ── Yin-Yang Wave Canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* Grid */
      const COLS = 16;
      const ROWS = 20;
      for (let c = 0; c <= COLS; c++) {
        const x = (c / COLS) * W;
        const pulse = Math.sin(t * 0.01 + c * 0.35) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.strokeStyle = `rgba(255,220,140,${0.012 + pulse * 0.015})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let r = 0; r <= ROWS; r++) {
        const y = (r / ROWS) * H;
        const pulse = Math.sin(t * 0.008 + r * 0.28) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.strokeStyle = `rgba(255,220,140,${0.008 + pulse * 0.012})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* Yin-Yang resonance rings — center left */
      const cx = W * 0.08;
      const cy = H * 0.4;
      for (let r = 0; r < 6; r++) {
        const radius = 15 + r * 25 + Math.sin(t * 0.012 + r * 0.8) * 6;
        const alpha = (0.06 - r * 0.008) * (Math.sin(t * 0.014 + r) * 0.5 + 0.5);
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* Trance wave lines */
      for (let w = 0; w < 4; w++) {
        ctx.beginPath();
        for (let x = 0; x <= W; x += 3) {
          const freq = 0.008 + w * 0.003;
          const amp = 12 + w * 8;
          const yOff = H * (0.2 + w * 0.18);
          const y = yOff + Math.sin(x * freq + t * 0.02 + w * 1.2) * amp;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        const alpha = 0.04 - w * 0.007;
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      /* DNA helix — right edge */
      const helixX = W * 0.96;
      const helixAmplitude = 18;
      const helixStep = 16;
      const helixCount = Math.ceil(H / helixStep) + 2;
      for (let i = 0; i < helixCount; i++) {
        const y = i * helixStep - (t * 0.35 % helixStep);
        const phase = (i / helixCount) * Math.PI * 8 + t * 0.016;
        const x1 = helixX + Math.sin(phase) * helixAmplitude;
        const x2 = helixX + Math.sin(phase + Math.PI) * helixAmplitude;
        const alpha = Math.abs(Math.sin(phase)) * 0.3 + 0.04;
        if (i > 0) {
          const prevY = (i - 1) * helixStep - (t * 0.35 % helixStep);
          const prevPhase = ((i - 1) / helixCount) * Math.PI * 8 + t * 0.016;
          const prevX1 = helixX + Math.sin(prevPhase) * helixAmplitude;
          ctx.beginPath();
          ctx.moveTo(prevX1, prevY);
          ctx.lineTo(x1, y);
          ctx.strokeStyle = `rgba(255,220,140,${alpha * 0.6})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
          const prevX2 = helixX + Math.sin(prevPhase + Math.PI) * helixAmplitude;
          ctx.beginPath();
          ctx.moveTo(prevX2, prevY);
          ctx.lineTo(x2, y);
          ctx.strokeStyle = `rgba(255,200,100,${alpha * 0.4})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
        if (i % 3 === 0) {
          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.strokeStyle = `rgba(255,220,140,${alpha * 0.2})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }

      /* Scan line */
      const scanY = ((t * 0.45) % H);
      const scanGrad = ctx.createLinearGradient(0, scanY - 60, 0, scanY + 60);
      scanGrad.addColorStop(0, "rgba(255,220,140,0)");
      scanGrad.addColorStop(0.5, "rgba(255,220,140,0.02)");
      scanGrad.addColorStop(1, "rgba(255,220,140,0)");
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 60, W, 120);

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".reveal");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateY(0)";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden" style={{ background: "#060606" }}>
      <SeoHead
        title="Iron | Artist Profile | MAVERICK vol.5"
        description="IronのアーティストプロフィールページÌ。東京拠点のテクノDJ。陰陽共鳴をベースにTrance Trip体験を追求するSAMSARAレジデント。2025年インドツアー完遂。"
        keywords="Iron, Tokyo techno DJ, SAMSARA, Trance Trip, underground techno Japan"
        canonical={`${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/artist/iron`}
        jsonLd={IRON_JSONLD}
      />
      <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none z-0" />

      {/* Back button */}
      <button
        onClick={() => navigate("/")}
        className="fixed top-7 left-7 md:left-12 z-50 flex items-center gap-3 group cursor-pointer whitespace-nowrap"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        <div className="w-8 h-8 flex items-center justify-center border rounded-full" style={{ borderColor: "rgba(255,220,140,0.2)" }}>
          <i className="ri-arrow-left-line" style={{ color: "rgba(255,220,140,0.5)", fontSize: "14px" }} />
        </div>
        <span className="text-[10px] tracking-[0.35em] uppercase hidden md:block" style={{ color: "rgba(255,220,140,0.3)" }}>
          Back
        </span>
      </button>

      <div ref={sectionRef} className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 pt-24 md:pt-32 pb-32">

        {/* ── HERO BLOCK ── */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start mb-24 md:mb-36">

          {/* Profile image */}
          <div
            className="reveal flex-shrink-0 relative w-full lg:w-auto"
            style={{ opacity: 0, transform: "translateY(24px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div className="absolute -inset-3 pointer-events-none" style={{ border: "1px solid rgba(255,220,140,0.08)", borderRadius: "2px" }} />
            <div className="absolute -top-1 -left-1 w-6 h-6 pointer-events-none" style={{ borderTop: "1px solid rgba(255,220,140,0.35)", borderLeft: "1px solid rgba(255,220,140,0.35)" }} />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 pointer-events-none" style={{ borderBottom: "1px solid rgba(255,220,140,0.35)", borderRight: "1px solid rgba(255,220,140,0.35)" }} />
            <div className="overflow-hidden w-full lg:w-auto" style={{ height: "clamp(260px, 55vw, 480px)", maxWidth: "360px" }}>
              <img
                src={PROFILE_IMAGE}
                alt="Iron"
                className="w-full h-full object-cover object-top"
                style={{ filter: "grayscale(15%) contrast(1.05)" }}
              />
            </div>
            <div className="flex items-center gap-2 mt-4" style={{ fontFamily: "'Courier New', monospace" }}>
              <div style={{ width: "12px", height: "1px", background: "rgba(255,220,140,0.25)" }} />
              <span className="text-[9px] tracking-[0.3em] uppercase" style={{ color: "rgba(255,220,140,0.2)" }}>
                JP — 35.6762° N
              </span>
            </div>
          </div>

          {/* Text block */}
          <div className="flex flex-col gap-8 flex-1 pt-2 lg:pt-6">
            <div
              className="reveal flex items-center gap-4"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
              <span className="text-[10px] tracking-[0.5em] uppercase" style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}>
                Artist Profile
              </span>
            </div>

            <div
              className="reveal"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <h1
                className="font-light leading-none tracking-wider"
                style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: "clamp(36px, 6vw, 80px)", fontWeight: 200, color: "rgba(255,255,255,0.92)", letterSpacing: "0.06em", textShadow: "0 0 60px rgba(255,220,140,0.1)" }}
              >
                Iron
              </h1>
              <div className="flex items-center gap-3 mt-3">
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "rgba(255,220,140,0.6)", boxShadow: "0 0 10px 2px rgba(255,200,100,0.25)" }} />
                <span className="text-xs tracking-[0.3em] uppercase" style={{ color: "rgba(255,220,140,0.35)", fontFamily: "'Courier New', monospace" }}>
                  Tokyo, Japan
                </span>
              </div>
            </div>

            <div
              className="reveal flex flex-wrap gap-2"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              {["Techno", "Trance Trip", "SAMSARA", "Yin-Yang", "Experimental"].map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-[10px] tracking-[0.25em] uppercase"
                  style={{ border: "1px solid rgba(255,220,140,0.12)", color: "rgba(255,220,140,0.35)", fontFamily: "'Courier New', monospace", borderRadius: "1px" }}
                >
                  {tag}
                </span>
              ))}
            </div>

            <div
              className="reveal"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <p
                className="leading-relaxed"
                style={{
                  fontSize: "clamp(15px, 1.1vw, 17px)",
                  color: "rgba(255,255,255,0.5)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.02em",
                  lineHeight: "2.0",
                }}
              >
                東京拠点。DJ。陰陽共鳴の摂理をベースにTrance Tripを追求し、Techno的なペースト状に独自のエクスペリメンタル性と様々なジャンル由来の力学を機能させることで、イデオロギーの拡大とバランス形成の双方を実現させるSetが特徴である。
              </p>
              <p
                className="leading-relaxed mt-5"
                style={{
                  fontSize: "clamp(15px, 1.1vw, 17px)",
                  color: "rgba(255,255,255,0.38)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.02em",
                  lineHeight: "2.0",
                }}
              >
                Residentとして所属するパーティー「SAMSARA」を中心にPeter Van Hoesen、Patrick Russell、Eric Cloutier、Sunju Hargunなど、多くの国外ゲストの前後プレイを経験。2025年、自身もGOA、Bangalore 2都市でのインドツアーを完遂。26年には韓国ツアーを控えるなど国外的な活動も増えている。
              </p>
            </div>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div
          className="reveal w-full mb-24 md:mb-32"
          style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease", height: "1px", background: "linear-gradient(to right, transparent, rgba(255,220,140,0.12), transparent)" }}
        />

        {/* ── TOURS ── */}
        <div className="mb-24 md:mb-32">
          <div
            className="reveal flex items-center gap-4 mb-12"
            style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
            <span className="text-[10px] tracking-[0.5em] uppercase" style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}>
              International Tours
            </span>
          </div>
          <div className="flex flex-col">
            {tours.map((tour, i) => (
              <div
                key={i}
                className="reveal group relative py-5 border-b overflow-hidden"
                style={{ borderColor: "rgba(255,220,140,0.06)", opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-0 group-hover:w-full transition-all duration-700 pointer-events-none" style={{ background: "linear-gradient(to right, rgba(255,220,140,0.03), transparent)" }} />
                <div className="flex items-center gap-6">
                  <span className="text-white/[0.12] flex-shrink-0" style={{ fontFamily: "'Courier New', monospace", fontSize: "11px", letterSpacing: "0.2em" }}>
                    {tour.year}
                  </span>
                  <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: "rgba(255,220,140,0.2)", flexShrink: 0 }} />
                  <span className="font-light tracking-wider" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: "clamp(14px, 1.8vw, 22px)", fontWeight: 300, color: "rgba(255,255,255,0.55)", letterSpacing: "0.05em" }}>
                    {tour.detail}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── GUEST ARTISTS ── */}
        <div className="mb-24 md:mb-32">
          <div
            className="reveal flex items-center gap-4 mb-12"
            style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
            <span className="text-[10px] tracking-[0.5em] uppercase" style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}>
              SAMSARA — Guest Artists
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {guests.map((guest, i) => (
              <div
                key={i}
                className="reveal py-4 px-5"
                style={{
                  opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease",
                  border: "1px solid rgba(255,220,140,0.07)",
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "rgba(255,220,140,0.25)" }} />
                </div>
                <span className="font-light text-sm" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, color: "rgba(255,255,255,0.45)", letterSpacing: "0.03em" }}>
                  {guest}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── MIX PLAYER ── */}
        <div className="mb-24 md:mb-32">
          <div
            className="reveal flex items-center gap-4 mb-10"
            style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
            <span
              className="text-[10px] tracking-[0.5em] uppercase"
              style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}
            >
              Listen
            </span>
          </div>

          <div
            className="reveal relative"
            style={{
              opacity: 0,
              transform: "translateY(20px)",
              transition: "opacity 1.2s ease, transform 1.2s ease",
              border: "1px solid rgba(255,220,140,0.08)",
            }}
          >
            <div className="absolute top-0 left-0 w-4 h-4 pointer-events-none" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute top-0 right-0 w-4 h-4 pointer-events-none" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute bottom-0 left-0 w-4 h-4 pointer-events-none" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute bottom-0 right-0 w-4 h-4 pointer-events-none" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />

            {/* SoundCloud embed */}
            <iframe
              title="Iron on SoundCloud"
              width="100%"
              height="450"
              allow="autoplay"
              src="https://w.soundcloud.com/player/?url=https%3A//on.soundcloud.com/7gRDScbxbmSbSTesnA&color=%23ffdc8c&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=true&visual=true"
              style={{ border: "none", display: "block", background: "#0a0a0a" }}
            />
          </div>

          <div
            className="reveal flex justify-end mt-4"
            style={{ opacity: 0, transform: "translateY(12px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <a
              href="https://on.soundcloud.com/7gRDScbxbmSbSTesnA"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
              style={{ textDecoration: "none" }}
            >
              <i className="ri-soundcloud-line" style={{ color: "rgba(255,220,140,0.3)", fontSize: "13px" }} />
              <span
                className="text-[9px] tracking-[0.35em] uppercase"
                style={{ color: "rgba(255,220,140,0.25)", fontFamily: "'Courier New', monospace" }}
              >
                View on SoundCloud
              </span>
              <i className="ri-external-link-line" style={{ color: "rgba(255,220,140,0.2)", fontSize: "10px" }} />
            </a>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div
          className="reveal w-full mb-24 md:mb-32"
          style={{
            opacity: 0,
            transform: "translateY(16px)",
            transition: "opacity 1.2s ease, transform 1.2s ease",
            height: "1px",
            background: "linear-gradient(to right, transparent, rgba(255,220,140,0.12), transparent)",
          }}
        />

        {/* ── QUOTE ── */}
        <div
          className="reveal relative mb-24 md:mb-32 px-5 md:px-16 py-10 md:py-16"
          style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease", border: "1px solid rgba(255,220,140,0.07)" }}
        >
          <div className="absolute top-0 left-0 w-5 h-5" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute top-0 right-0 w-5 h-5" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute bottom-0 left-0 w-5 h-5" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute bottom-0 right-0 w-5 h-5" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />
          <p
            className="text-center font-light leading-relaxed mb-6"
            style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: "clamp(14px, 1.8vw, 22px)", fontWeight: 200, color: "rgba(255,255,255,0.55)", letterSpacing: "0.04em", lineHeight: "1.8", fontStyle: "italic" }}
          >
            &ldquo;陰陽共鳴の摂理をベースに、イデオロギーの拡大とバランス形成の双方を実現させる&rdquo;
          </p>
          <p className="text-center text-[10px] tracking-[0.4em] uppercase" style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}>
            — Iron
          </p>
        </div>

        {/* ── BACK ── */}
        <div className="reveal flex justify-center" style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-4 px-8 py-4 cursor-pointer whitespace-nowrap"
            style={{ border: "1px solid rgba(255,220,140,0.18)", fontFamily: "'Courier New', monospace", color: "rgba(255,220,140,0.4)", fontSize: "11px", letterSpacing: "0.35em", textTransform: "uppercase", background: "transparent" }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: "12px" }} />
            Back to Lineup
          </button>
        </div>
      </div>
    </div>
  );
}
